import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { ClientSettingsApiService } from '../../../../../core/services/api/client-settings-api.service';
import { ReportModel } from '../../../../../core/models/client-settings.model';
import { ConfirmationService, Modal } from '../../../../../shared';

interface ReportModelOption {
  value: ReportModel;
  labelKey: string;
  icon: string;
  // Caminho do PDF de referência do modelo (null quando ainda não há modelo, ex: GRI).
  pdfUrl: string | null;
}

@Component({
  selector: 'app-report-model-modal',
  imports: [CommonModule, TranslocoPipe, Modal, NgxExtendedPdfViewerModule],
  templateUrl: './report-model-modal.html',
  styleUrl: './report-model-modal.scss',
})
export class ReportModelModal implements OnInit {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly clientSettingsApi = inject(ClientSettingsApiService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly transloco = inject(TranslocoService);

  // Recebe o ID do cliente do modal de edição (igual ao padrão do customer-edit).
  readonly clientId = signal<string>('');

  readonly selected = signal<ReportModel | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly errorMessage = signal<string>('');

  readonly options: ReportModelOption[] = [
    // {
    //   value: 'GRI',
    //   labelKey: 'admin.customers.reportModel.options.gri',
    //   icon: 'bi bi-file-earmark-text',
    //   pdfUrl: 'assets/report-models/gri.pdf',
    // },
    // {
    //   value: 'SASB',
    //   labelKey: 'admin.customers.reportModel.options.sasb',
    //   icon: 'bi bi-file-earmark-text',
    //   pdfUrl: 'assets/report-models/sasb.pdf',
    // },
    {
      value: 'Hibrido',
      labelKey: 'admin.customers.reportModel.options.hibrido',
      icon: 'bi bi-files',
      pdfUrl: 'assets/report-models/hibrido.pdf',
    },
    // {
    //   value: 'BancoDoBrasil',
    //   labelKey: 'admin.customers.reportModel.options.bancoDoBrasil',
    //   icon: 'bi bi-bank',
    //   pdfUrl: 'assets/report-models/banco-do-brasil.pdf',
    // },
  ];

  // Caminho do PDF do modelo selecionado (viewer + abrir em nova guia). Null quando não há prévia.
  readonly selectedPdfUrl = computed<string | null>(() => {
    const value = this.selected();
    return this.options.find((o) => o.value === value)?.pdfUrl ?? null;
  });

  openInNewTab(): void {
    const pdf = this.selectedPdfUrl();
    if (pdf) globalThis.window.open(pdf, '_blank', 'noopener');
  }

  ngOnInit(): void {
    void this.loadSettings();
  }

  private async loadSettings(): Promise<void> {
    const id = this.clientId();
    if (!id) return;

    this.isLoading.set(true);

    try {
      await this.clientSettingsApi.getByClientId(id);

      this.selected.set('Hibrido');
    } finally {
      this.isLoading.set(false);
    }
  }

  select(value: ReportModel): void {
    this.selected.set(value);
  }

  async onConfirm(): Promise<void> {
    const value = this.selected();
    // Validação: só salva com uma opção selecionada (o botão já fica desabilitado).
    if (!value) return;

    this.errorMessage.set('');
    this.isSaving.set(true);
    try {
      await this.clientSettingsApi.updateReportModel(this.clientId(), value);
      this.activeModal.close(true);
      await new Promise((resolve) => setTimeout(resolve, 200));
      await this.confirmationService.showInfo(
        this.transloco.translate('admin.customers.reportModel.savedTitle'),
        this.transloco.translate('admin.customers.reportModel.savedMessage'),
        'success',
      );
    } catch {
      this.errorMessage.set(this.transloco.translate('admin.customers.reportModel.errorDefault'));
    } finally {
      this.isSaving.set(false);
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
